/**
 * TaskSynchronizationService
 * 
 * Handles bidirectional synchronization between Calendar and Epic Journey views
 * ensuring that changes in one view are immediately reflected in the other.
 */

import StatusMappingService from './StatusMappingService.js';
import DateSynchronizationService from './DateSynchronizationService.js';
import { supabase } from '../lib/supabase.js';

class TaskSynchronizationService {
  /**
   * Normalize priority values to ensure database constraint compliance
   */
  static normalizePriority(priority) {
    if (!priority) return 'medium';
    const validPriorities = ['critical', 'high', 'medium', 'low'];
    if (validPriorities.includes(priority)) return priority;
    
    const mapping = {
      'urgent': 'critical',
      'normal': 'medium',
      'important': 'high'
    };
    
    return mapping[priority] || 'medium';
  }

  /**
   * Normalize source_type values to ensure database constraint compliance
   */
  static normalizeSourceType(sourceType) {
    if (!sourceType) return 'custom';
    
    // Valid source types from actual database constraint
    const validSourceTypes = [
      'milestone', 'custom', 'manual', 'college_deadline', 'milestone_template', 
      'milestone_subtask', 'profile_generation', 'essay_generation', 'fafsa', 
      'css_profile', 'strategic_milestone', 'system_generated', 'user_created', 
      'deadline', 'task', 'event', 'reminder'
    ];
    
    // If it's already valid, return as-is
    if (validSourceTypes.includes(sourceType)) return sourceType;
    
    // Mapping for common invalid values
    const mapping = {
      'action': 'task',                // Next actions are tasks
      'suggestion': 'system_generated', // AI suggestions are system generated
      'preparation': 'task',           // Preparation tasks are tasks
      'strategic': 'strategic_milestone', // Strategic items are strategic milestones
      'message': 'user_created',       // Message-based tasks are user created
      'calendar': 'event',             // Calendar items are events
      'shopping': 'user_created',      // Shopping items are user created
      'family_activity': 'event'       // Family activities are events
    };
    
    return mapping[sourceType] || 'custom';
  }
  /**
   * Subscription channels for real-time sync
   */
  static subscriptions = new Map();

  /**
   * Initialize real-time synchronization for a user
   */
  static initializeSync(userId, onUpdate) {
    // Clean up existing subscription if any
    this.cleanupSync(userId);

    // Create new subscription
    const channel = supabase
      .channel(`task_sync_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'timeline_items',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          this.handleRealtimeUpdate(payload, onUpdate);
        }
      )
      .subscribe();

    this.subscriptions.set(userId, channel);
    
    return channel;
  }

  /**
   * Clean up synchronization subscription
   */
  static cleanupSync(userId) {
    const channel = this.subscriptions.get(userId);
    if (channel) {
      channel.unsubscribe();
      this.subscriptions.delete(userId);
    }
  }

  /**
   * Handle real-time updates
   */
  static handleRealtimeUpdate(payload, onUpdate) {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    // Ensure status and date fields are synchronized
    if (eventType === 'UPDATE') {
      const needsSync = this.checkIfNeedsSync(oldRecord, newRecord);
      if (needsSync) {
        // Prevent infinite loop by checking if fields are already synced
        const syncedData = this.getSynchronizedData(newRecord);
        if (JSON.stringify(syncedData) !== JSON.stringify(newRecord)) {
          this.syncTask(newRecord.id, syncedData);
        }
      }
    }
    
    // Notify UI components of the update
    if (onUpdate) {
      onUpdate({
        type: eventType,
        task: newRecord || oldRecord,
        changes: this.getChangedFields(oldRecord, newRecord)
      });
    }
  }

  /**
   * Check if a task update needs synchronization
   */
  static checkIfNeedsSync(oldRecord, newRecord) {
    if (!oldRecord || !newRecord) return false;
    
    // Check if status fields changed
    const statusChanged = 
      oldRecord.workflow_phase !== newRecord.workflow_phase ||
      oldRecord.status !== newRecord.status ||
      oldRecord.application_status !== newRecord.application_status;
    
    // Check if date fields changed
    const dateChanged = 
      oldRecord.due_date !== newRecord.due_date ||
      oldRecord.scheduled_date !== newRecord.scheduled_date;
    
    return statusChanged || dateChanged;
  }

  /**
   * Get synchronized data for a task
   */
  static getSynchronizedData(task) {
    const syncedData = { ...task };
    
    // Synchronize status fields
    if (task.workflow_phase || task.status || task.application_status) {
      const sourceField = task.workflow_phase ? 'workflow_phase' : 
                          task.status ? 'status' : 
                          'application_status';
      const sourceValue = task[sourceField];
      const syncedStatus = StatusMappingService.getSynchronizedStatus(sourceField, sourceValue);
      Object.assign(syncedData, syncedStatus);
    }
    
    // Synchronize date fields
    if (task.due_date || task.scheduled_date) {
      const sourceField = task.due_date && !task.scheduled_date ? 'due_date' : 'scheduled_date';
      const sourceValue = task[sourceField];
      const syncedDates = DateSynchronizationService.getSynchronizedDates(
        sourceField, 
        sourceValue, 
        task.category || 'general'
      );
      Object.assign(syncedData, syncedDates);
    }
    
    return syncedData;
  }

  /**
   * Sync a task (update synchronized fields)
   */
  static async syncTask(taskId, syncedData) {
    try {
      const { error } = await supabase
        .from('timeline_items')
        .update({
          ...syncedData,
          sync_updated_at: new Date().toISOString()
        })
        .eq('id', taskId);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error syncing task:', error);
      return false;
    }
  }

  /**
   * Update task from Calendar view
   */
  static async updateFromCalendar(taskId, updates) {
    try {
      // Get synchronized status if status changed
      let syncedUpdates = { ...updates };
      
      if (updates.status) {
        const syncedStatus = StatusMappingService.getSynchronizedStatus('status', updates.status);
        syncedUpdates = { ...syncedUpdates, ...syncedStatus };
      }
      
      // Get synchronized dates if dates changed
      if (updates.scheduled_date) {
        const syncedDates = DateSynchronizationService.getSynchronizedDates(
          'scheduled_date',
          updates.scheduled_date,
          updates.category || 'general'
        );
        syncedUpdates = { ...syncedUpdates, ...syncedDates };
      }
      
      // Update task with synchronized fields
      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          ...syncedUpdates,
          updated_from: 'calendar',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error updating task from Calendar:', error);
      throw error;
    }
  }

  /**
   * Update task from Epic Journey view
   */
  static async updateFromEpic(taskId, updates) {
    try {
      // Get synchronized status if workflow_phase changed
      let syncedUpdates = { ...updates };
      
      if (updates.workflow_phase) {
        const syncedStatus = StatusMappingService.getSynchronizedStatus('workflow_phase', updates.workflow_phase);
        syncedUpdates = { ...syncedUpdates, ...syncedStatus };
      }
      
      // Get synchronized dates if dates changed
      if (updates.due_date) {
        const syncedDates = DateSynchronizationService.getSynchronizedDates(
          'due_date',
          updates.due_date,
          updates.category || 'general'
        );
        syncedUpdates = { ...syncedUpdates, ...syncedDates };
      } else if (updates.scheduled_date) {
        // When updating scheduled_date, only update that field (don't touch due_date)
        syncedUpdates.scheduled_date = updates.scheduled_date;
      }
      
      // Update task with synchronized fields
      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          ...syncedUpdates,
          updated_from: 'epic',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error updating task from Epic:', error);
      throw error;
    }
  }

  /**
   * Create task with proper synchronization
   */
  static async createTask(taskData, sourceView = 'unknown') {
    try {
      // Ensure all status fields are synchronized
      const statusSource = taskData.workflow_phase ? 'workflow_phase' :
                          taskData.status ? 'status' :
                          'workflow_phase'; // default
      const statusValue = taskData[statusSource] || 'ready';
      const syncedStatus = StatusMappingService.getSynchronizedStatus(statusSource, statusValue);
      
      // Ensure all date fields are synchronized
      const dateSource = taskData.due_date ? 'due_date' :
                        taskData.scheduled_date ? 'scheduled_date' :
                        null;
      let syncedDates = {};
      if (dateSource) {
        syncedDates = DateSynchronizationService.getSynchronizedDates(
          dateSource,
          taskData[dateSource],
          taskData.category || 'general'
        );
      }
      
      const normalizedData = {
        ...taskData,
        ...syncedStatus,
        ...syncedDates,
        priority: TaskSynchronizationService.normalizePriority(taskData.priority),
        source_type: TaskSynchronizationService.normalizeSourceType(taskData.source_type),
        created_from: sourceView,
        created_at: new Date().toISOString()
      };
      
      console.log('🔧 TaskSync creating task with normalized data:', normalizedData);
      
      // Create task with all synchronized fields and normalization
      const { data, error } = await supabase
        .from('timeline_items')
        .insert(normalizedData)
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error creating synchronized task:', error);
      throw error;
    }
  }

  /**
   * Batch update tasks with synchronization
   */
  static async batchUpdateTasks(taskIds, updates, sourceView = 'unknown') {
    try {
      // Get synchronized updates
      let syncedUpdates = { ...updates };
      
      if (updates.status || updates.workflow_phase) {
        const sourceField = updates.workflow_phase ? 'workflow_phase' : 'status';
        const syncedStatus = StatusMappingService.getSynchronizedStatus(
          sourceField, 
          updates[sourceField]
        );
        syncedUpdates = { ...syncedUpdates, ...syncedStatus };
      }
      
      if (updates.due_date || updates.scheduled_date) {
        const sourceField = updates.due_date ? 'due_date' : 'scheduled_date';
        const syncedDates = DateSynchronizationService.getSynchronizedDates(
          sourceField,
          updates[sourceField],
          'general'
        );
        syncedUpdates = { ...syncedUpdates, ...syncedDates };
      }
      
      // Update all tasks
      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          ...syncedUpdates,
          updated_from: sourceView,
          updated_at: new Date().toISOString()
        })
        .in('id', taskIds)
        .select();
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error batch updating tasks:', error);
      throw error;
    }
  }

  /**
   * Get changed fields between two records
   */
  static getChangedFields(oldRecord, newRecord) {
    if (!oldRecord || !newRecord) return [];
    
    const changes = [];
    const fields = Object.keys(newRecord);
    
    fields.forEach(field => {
      if (oldRecord[field] !== newRecord[field]) {
        changes.push({
          field,
          oldValue: oldRecord[field],
          newValue: newRecord[field]
        });
      }
    });
    
    return changes;
  }

  /**
   * Validate task consistency
   */
  static validateTaskConsistency(task) {
    const issues = [];
    
    // Check status consistency
    if (task.workflow_phase && task.status) {
      const expectedStatus = StatusMappingService.workflowToStatus(task.workflow_phase);
      if (task.status !== expectedStatus) {
        issues.push({
          type: 'status_mismatch',
          message: `Status '${task.status}' doesn't match workflow_phase '${task.workflow_phase}'`,
          expected: expectedStatus,
          actual: task.status
        });
      }
    }
    
    // Check date consistency
    if (task.scheduled_date && task.due_date) {
      const validation = DateSynchronizationService.validateDates(
        task.scheduled_date,
        task.due_date
      );
      if (!validation.isValid) {
        issues.push({
          type: 'date_conflict',
          message: validation.errors.join(', '),
          scheduled: task.scheduled_date,
          due: task.due_date
        });
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  }

  /**
   * Fix task inconsistencies
   */
  static async fixTaskInconsistencies(taskId) {
    try {
      // Get the task
      const { data: task, error: fetchError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('id', taskId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Get synchronized data
      const syncedData = this.getSynchronizedData(task);
      
      // Update if needed
      const needsUpdate = JSON.stringify(syncedData) !== JSON.stringify(task);
      if (needsUpdate) {
        const { data: updated, error: updateError } = await supabase
          .from('timeline_items')
          .update({
            ...syncedData,
            consistency_fixed_at: new Date().toISOString()
          })
          .eq('id', taskId)
          .select()
          .single();
        
        if (updateError) throw updateError;
        
        return {
          fixed: true,
          task: updated
        };
      }
      
      return {
        fixed: false,
        task: task,
        message: 'Task is already consistent'
      };
    } catch (error) {
      console.error('Error fixing task inconsistencies:', error);
      throw error;
    }
  }
}

export default TaskSynchronizationService;